import type { FlowerBatch } from '../types';

export const FLOWER_BATCH_LIMIT = 100;
export const FLOWER_QUEUE_LIMIT = 200;
export const FLOWER_STORAGE_KEY = 'panther-2026:flower-queue:v1';

export interface QueueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function validBatch(value: unknown): value is FlowerBatch {
  if (!value || typeof value !== 'object') return false;
  const batch = value as Partial<FlowerBatch>;
  return (
    typeof batch.batchId === 'string' &&
    typeof batch.topicId === 'string' &&
    Number.isInteger(batch.delta) &&
    (batch.delta ?? 0) > 0 &&
    (batch.delta ?? 0) <= FLOWER_BATCH_LIMIT &&
    typeof batch.sealed === 'boolean' &&
    typeof batch.createdAt === 'number' &&
    Number.isInteger(batch.attempts) &&
    (batch.attempts ?? -1) >= 0 &&
    (batch.generation === undefined ||
      (Number.isSafeInteger(batch.generation) && (batch.generation ?? -1) >= 0))
  );
}

export class FlowerQueue {
  private batches: FlowerBatch[];
  private generation = 0;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly storage: QueueStorage) {
    this.batches = this.read();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  snapshot(): readonly FlowerBatch[] {
    return this.batches;
  }

  pendingFor(topicId: string): number {
    return this.batches
      .filter((batch) => batch.topicId === topicId)
      .reduce((total, batch) => total + batch.delta, 0);
  }

  setGeneration(generation: number): boolean {
    const kept = this.batches.filter((batch) => (batch.generation ?? 0) === generation);
    const discarded = kept.length !== this.batches.length;
    this.batches = kept;
    this.generation = generation;
    if (discarded) this.commit();
    return discarded;
  }

  enqueue(topicId: string): FlowerBatch {
    const open = [...this.batches]
      .reverse()
      .find(
        (batch) =>
          batch.topicId === topicId &&
          !batch.sealed &&
          batch.delta < FLOWER_BATCH_LIMIT,
      );

    if (open) {
      this.batches = this.batches.map((batch) =>
        batch.batchId === open.batchId
          ? { ...batch, delta: batch.delta + 1 }
          : batch,
      );
      this.commit();
      return this.batches.find((batch) => batch.batchId === open.batchId)!;
    }

    if (this.batches.length >= FLOWER_QUEUE_LIMIT) {
      throw new Error('Flower queue is full. Reconnect before sending more.');
    }

    const batch: FlowerBatch = {
      batchId: crypto.randomUUID(),
      topicId,
      delta: 1,
      sealed: false,
      createdAt: Date.now(),
      attempts: 0,
      generation: this.generation,
    };
    this.batches = [...this.batches, batch];
    this.commit();
    return batch;
  }

  takeNext(): FlowerBatch | null {
    const next = this.batches.find((batch) => batch.sealed) ??
      this.batches.find((batch) => !batch.sealed);
    if (!next) return null;

    const sealed = { ...next, sealed: true, attempts: next.attempts + 1 };
    this.batches = this.batches.map((batch) =>
      batch.batchId === sealed.batchId ? sealed : batch,
    );
    this.commit();
    return sealed;
  }

  acknowledge(batchId: string): void {
    this.batches = this.batches.filter((batch) => batch.batchId !== batchId);
    this.commit();
  }

  private read(): FlowerBatch[] {
    try {
      const parsed: unknown = JSON.parse(
        this.storage.getItem(FLOWER_STORAGE_KEY) ?? '[]',
      );
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(validBatch).slice(0, FLOWER_QUEUE_LIMIT);
    } catch {
      return [];
    }
  }

  private commit(): void {
    this.storage.setItem(FLOWER_STORAGE_KEY, JSON.stringify(this.batches));
    this.listeners.forEach((listener) => listener());
  }
}
